import { engineComponent } from "neo-common";
import { Communication } from "neo-games";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { ContainerElement } from "../../Components/Elements/Graphics/ContainerElement";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../../Utils/FrameTimer";
import { Paylines } from "../Paylines";
import { Symbols } from "../Symbols";
import { Machine } from "../Machine";
import { SwitchElement } from "../../Components/Elements/Graphics/SwitchElement";
import { StageController } from "../../Components/StageController";
import { WinAnimator } from "../WinAnimator";
import { FreespinsController } from "./FreespinsController";
import { GameManager } from "../../Managers/GameManager";
import { PlayList } from "../../Components/Sounds/PlayList";
import { HtmlController } from "../../HTMLComponents/HtmlController";
import { DataManager } from "../../Managers/DataManager";
import { Tweens } from "../../Components/Effects/Tweens";
import { WinAnimatorExtendedFS } from "../../CustomComponents/WinAnimatorExtendedFS";
import { GroupTweens } from "../../CustomComponents/GroupTweens";
import { AmbientBackground } from "../../Utils/AmbientBackground";
import { MachineExtended } from "../../CustomComponents/MachineExtended";
import { Symbol } from "../Symbol";
import { ModuleManager } from "../../Managers/ModuleManager";
import { SymbolExtended } from "../../CustomComponents/SymbolExtended";
import { GameModules } from "neo-games/Engine/Communication/Game/GameModules";
import { ImageElement } from "../../Components/Elements/Graphics/ImageElement";
import { TimelineElement } from "../../Components/Elements/Graphics/TimelineElement";
import { Scaling } from "../../Utils/Scaling";

@engineComponent(ComponentTypes.DisplayObject)
export class FreespinsView extends ContainerElement {
    static INTRO_DELAY: number = 0;
    static OUTRO_DELAY: number = 30;

    private _containerHeight: number = 1080;

    private _controller: FreespinsController;

    private _introDelay: FrameTimerEventEmitter;
    private _outroDelay: FrameTimerEventEmitter;
    private _enableDelay: FrameTimerEventEmitter;
    private _retriggerDelay: FrameTimerEventEmitter;

    private _paylines: Paylines;
    private _symbols: Symbols;
    private _machine: MachineExtended;
    private _winAnimator: WinAnimatorExtendedFS;
    private _noWinContainer: ContainerElement;
    private _winContainer: ContainerElement;
    private _groupTweens: SwitchElement;
    private _blurContainer: SwitchElement;
    private _respinFX: TimelineElement;
    private _middleground: SwitchElement;

    private _fsMessageStart: HTMLElement;
    private _fsMessageEnd: HTMLElement;
    private _fsMessageEndNoWin: HTMLElement;

    private modeSetting: { Landscape?: Node; Portrait?: Node; Extended?: Node } = {};

    public static wasRespin: boolean = false;

    constructor() {
        super();

        this._introDelay = CreateFrameTimer("introDelay");
        this._introDelay.on(FrameTimerEvents.Complete, this.introFinished.bind(this));

        this._outroDelay = CreateFrameTimer("outroDelay");
        this._outroDelay.on(FrameTimerEvents.Complete, this.outroFinished.bind(this));

        this._enableDelay = CreateFrameTimer("enableDelay");
        this._enableDelay.on(FrameTimerEvents.Complete, this.enableDelay.bind(this));

        this._retriggerDelay = CreateFrameTimer("retriggerDelay");
        this._retriggerDelay.on(FrameTimerEvents.Complete, this.retriggerDelay.bind(this));

        StageController.freespinsStageContainer = this;
    }

    create(node: Element): void {
        super.create(node);

        if (node.hasAttribute("Height")) {
            this._containerHeight = parseInt(node.getAttribute("Height"));
        }

        if (this.childs != null) {
            for (let key in this.childs) {
                if (this.childs[key] instanceof Paylines) {
                    this._paylines = this.childs[key];
                    this._paylines.reset();
                }

                if (this.childs[key] instanceof Symbols) {
                    this._symbols = this.childs[key];
                    this.removeChild(this._symbols);
                }

                if (this.childs[key] instanceof MachineExtended) {
                    this._machine = this.childs[key];
                }

                if (this.childs[key] instanceof WinAnimatorExtendedFS) {
                    this._winAnimator = this.childs[key];
                }

                if (key === "BlurContainer" && this.childs[key] instanceof SwitchElement) {
                    this._blurContainer = this.childs[key];
                }

                if (key === "RespinFX" && this.childs[key] instanceof TimelineElement) {
                    this._respinFX = this.childs[key];
                }

                if (key === "NoWinContainer" && this.childs[key] instanceof ContainerElement) {
                    this._noWinContainer = this.childs[key];
                }

                if (key === "WinContainer" && this.childs[key] instanceof ContainerElement) {
                    this._winContainer = this.childs[key];
                }

                if (key === "Middleground" && this.childs[key] instanceof SwitchElement) {
                    this._middleground = <SwitchElement>this.childs[key];
                }

                if (key === "GroupTweens" && this.childs[key] instanceof SwitchElement) {
                    this._groupTweens = this.childs[key]; // SwitchElement with 2 elements of GroupTweens
                }
            }

            // set childs to GroupTweens object
            if (this._groupTweens) {
                let groupTweenElements: Object = this._groupTweens.getChildElements();
                if (groupTweenElements) {
                    for (let key in groupTweenElements) {
                        if (groupTweenElements.hasOwnProperty(key)) {
                            if (groupTweenElements[key] instanceof GroupTweens) {
                                (<GroupTweens>groupTweenElements[key]).setChilds(this.childs);
                            }
                        }
                    }
                }
            }

            this._fsMessageStart = document.getElementById("fsMessageStart");
            this._fsMessageEnd = document.getElementById("fsMessageEnd");
            this._fsMessageEndNoWin = document.getElementById("fsMessageEndNoWin");

            //prevent first animation on page load
            this._fsMessageStart.classList.add("_noAnim");
            this._fsMessageEnd.classList.add("_noAnim");
            this._fsMessageEndNoWin.classList.add("_noAnim");

            if (DEBUG) {
                if (this._paylines == null) {
                    this.log("WARNING no paylines");
                }

                if (this._symbols == null) {
                    this.log("ERROR no symbols");
                }

                if (this._machine == null) {
                    this.log("ERROR no machine");
                }

                if (this._winAnimator == null) {
                    this.log("WARNING no winAnimator");
                }

                if (this._fsMessageStart == null) {
                    this.log("ERROR no fsMessageStart");
                }

                if (this._fsMessageEnd == null) {
                    this.log("ERROR no fsMessageEnd");
                }

                if (this._fsMessageEndNoWin == null) {
                    this.log("ERROR no fsMessageEndNoWin");
                }
            }
        }

        for (let i = 0; i < node.childNodes.length; i++) {
            const child = node.childNodes[i];
            if (child.nodeName === "Landscape") {
                this.modeSetting.Landscape = child;
            }
            if (child.nodeName === "Portrait") {
                this.modeSetting.Portrait = child;
            }
            if (child.nodeName === "Extended") {
                this.modeSetting.Extended = child;
            }
        }

        Scaling.registerResizeCallback(this.setMode.bind(this));
    }

    initReceived(controller: FreespinsController): void {
        if (DEBUG) {
            this.log("initReceived");
        }

        this._controller = controller;

        if (this._symbols != null) {
            if (this._machine != null) {
                if (GameManager.freespinsResponse != null) {
                    if (
                        GameManager.freespinsResponse.initData != null &&
                        GameManager.freespinsResponse.initData.reels !== null
                    ) {
                        this._machine.on(Machine.ReelStopped, this.onReelStopped.bind(this));
                        this._machine.on(Machine.MachineStopped, this.machineStopped.bind(this));
                        this._machine.initialize(
                            this._symbols,
                            GameManager.freespinsResponse.initData.reels,
                            this._winAnimator,
                            this.childs["Teasing"],
                            this.childs["NoTrimContainer"],
                        );
                    }

                    if (this._winAnimator != null) {
                        this._winAnimator.on(WinAnimator.FinishWinCalculation, this.finishWinCalculation.bind(this));
                        this._winAnimator.on(WinAnimator.FinishWinAnimator, this.finishWinAnimator.bind(this));
                        this._winAnimator.initialize(
                            this._symbols,
                            this._paylines,
                            this._noWinContainer,
                            this._winContainer,
                            this,
                        );
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no freespin reponse");
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no symbols");
            }
        }

        this.visible = false;
    }

    initIntro(): void {
        StageController.baseStage = false;
        StageController.freespinStage = true;

        // this.allSwitchElementToCase("INIT_INTRO");
        if (
            (GameManager.freespinsResponse &&
                GameManager.freespinsResponse.customData &&
                GameManager.freespinsResponse.customData.respin == true) ||
            (GameManager.freespinsResponse.freeSpins.totalCount == 1 &&
                GameManager.freespinsResponse.freeSpins.remainingCount == 1)
        ) {
            FreespinsView.wasRespin = true;
            if (GameManager.freespinsResponse.freeSpins.remainingCount > 0) {
                ModuleManager.showMessage(Communication.Game.GameModules.Freespins, "RESPIN_INIT");
            }
        }

        this._machine.setStoppedSymbolsArray(GameManager.freespinsResponse.screen.reels);
        this._winAnimator.showSymbols(GameManager.freespinsResponse.screen.reels);
        this._winAnimator.showInitWins(GameManager.freespinsResponse.wins, GameManager.freespinsResponse.currentWin);

        this.visible = true;

        AmbientBackground.changeAmbientBackground("color-1", 0);

        this.retrigger(true);

        if (this.isLastFreespin()) {
            if (!FreespinsView.wasRespin) {
                PlayList.stop("SND_FSLoop");
                PlayList.loop("SND_FSWait");
                PlayList.play("SND_FSEnd");

                if (GameManager.freespinsResponse.totalWin > 0) {
                    if (
                        this._fsMessageEnd != null &&
                        this._fsMessageEnd.classList.contains(HtmlController.DisabledClass)
                    ) {
                        this._fsMessageEnd.classList.remove(HtmlController.DisabledClass);
                        this._fsMessageEnd.classList.remove("_noAnim");
                    }
                } else {
                    if (
                        this._fsMessageEndNoWin != null &&
                        this._fsMessageEndNoWin.classList.contains(HtmlController.DisabledClass)
                    ) {
                        this._fsMessageEndNoWin.classList.remove(HtmlController.DisabledClass);
                        this._fsMessageEndNoWin.classList.remove("_noAnim");
                    }
                }
                ModuleManager.showMessage(Communication.Game.GameModules.Freespins, "END");
            }
        } else {
            PlayList.loop("SND_FSLoop");
        }
    }

    spin(afterRetrigger: boolean = false): void {
        StageController.baseStage = false;
        StageController.freespinStage = true;

        PlayList.stop("SND_BaseRecapLoop");
        PlayList.resetVolume("SND_BaseLoop");
        PlayList.loop("SND_FSLoop", 15).resetVolume();

        if (this._fsMessageStart != null && !this._fsMessageStart.classList.contains(HtmlController.DisabledClass)) {
            this._fsMessageStart.classList.add(HtmlController.DisabledClass);
            ModuleManager.hideMessage(Communication.Game.GameModules.Freespins);
        }

        this._machine.spinStart(this._winAnimator.reset(true, true), true, this._winAnimator.currentSymbols);
    }

    stop(): void {
        this._machine.spinStop(null, true);
    }

    startReceived(): void {
        this._enableDelay.stop();
        if (this._fsMessageStart != null && !this._fsMessageStart.classList.contains(HtmlController.DisabledClass)) {
            this._fsMessageStart.classList.add(HtmlController.DisabledClass);
        }

        if (GameManager.freespinsResponse.freeSpins.remainingCount != 1) {
            ModuleManager.hideMessage(Communication.Game.GameModules.Base);
        }

        PlayList.stop("SND_FSWait");
        PlayList.stop("SND_FSWaitIn");

        this._machine.setStoppedSymbolsArray(GameManager.freespinsResponse.screen.reels);
        this._winAnimator.showSymbols(GameManager.freespinsResponse.screen.reels);
        this._winAnimator.reset(true);
    }

    spinReceived(): void {
        this._machine.setStoppedSymbolsArray(GameManager.freespinsResponse.screen.reels);
        this._machine.spinStop(null, false, true);
    }

    onReelStopped(): void {
        this._winAnimator.onReelStopped(GameManager.freespinsResponse.screen.reels, this._machine.actualReelStopped);
    }

    public getRandom(min: number, max: number): number {
        return Math.random() * (max - min) + min;
    }

    machineStopped(): void {
        this._controller.machineStopped();

        if (
            !FreespinsView.wasRespin ||
            (GameManager.freespinsResponse &&
                GameManager.freespinsResponse.freeSpins &&
                GameManager.freespinsResponse.freeSpins.remainingCount > 0)
        ) {
            if (
                this._winAnimator.showWins(GameManager.freespinsResponse.wins, GameManager.freespinsResponse.currentWin)
            ) {
                PlayList.stop("SND_FSLoop");
            }
        }

        if (GameManager.freespinsResponse.currentWin === 0) {
            if (this.isLastFreespin()) {
                PlayList.stop("SND_FSLoop");
                PlayList.loop("SND_FSWait");
                PlayList.play("SND_FSEnd");

                if (!FreespinsView.wasRespin) {
                    if (GameManager.freespinsResponse.totalWin > 0) {
                        if (
                            this._fsMessageEnd != null &&
                            this._fsMessageEnd.classList.contains(HtmlController.DisabledClass)
                        ) {
                            this._fsMessageEnd.classList.remove(HtmlController.DisabledClass);
                            this._fsMessageEnd.classList.remove("_noAnim");
                        }
                    } else {
                        if (
                            this._fsMessageEndNoWin != null &&
                            this._fsMessageEndNoWin.classList.contains(HtmlController.DisabledClass)
                        ) {
                            this._fsMessageEndNoWin.classList.remove(HtmlController.DisabledClass);
                            this._fsMessageEndNoWin.classList.remove("_noAnim");
                        }
                    }
                    ModuleManager.showMessage(Communication.Game.GameModules.Freespins, "END");
                }
            }
        }
    }

    finishWinCalculation(): void {
        this._controller.winCalculationFinished();
    }

    finishWinAnimator(): void {
        this._controller.allWinsFinished();

        if (this.isLastFreespin()) {
            PlayList.stop("SND_FSLoop");
            PlayList.loop("SND_FSWait");
            PlayList.play("SND_FSEnd");

            if (!FreespinsView.wasRespin) {
                if (GameManager.freespinsResponse.totalWin > 0) {
                    if (
                        this._fsMessageEnd != null &&
                        this._fsMessageEnd.classList.contains(HtmlController.DisabledClass)
                    ) {
                        this._fsMessageEnd.classList.remove(HtmlController.DisabledClass);
                        this._fsMessageEnd.classList.remove("_noAnim");
                    }
                } else {
                    if (
                        this._fsMessageEndNoWin != null &&
                        this._fsMessageEndNoWin.classList.contains(HtmlController.DisabledClass)
                    ) {
                        this._fsMessageEndNoWin.classList.remove(HtmlController.DisabledClass);
                        this._fsMessageEndNoWin.classList.remove("_noAnim");
                    }
                }

                ModuleManager.showMessage(Communication.Game.GameModules.Freespins, "END");
            }
        } else {
            // PlayList.loop('SND_FSLoop');
            let wins = GameManager.freespinsResponse.wins;
            let fsWin =
                wins &&
                wins.length > 0 &&
                wins.filter((w) => w.type == Communication.Game.WinType.freeSpins).length > 0;
            if (!DataManager.Autoplay && !fsWin) {
                // PlayList.stop('SND_FSLoop');
            }
        }

        this.retrigger();
    }

    intro(long: boolean = false, previousModule: string): void {
        StageController.freespinStage = true;

        if (this._groupTweens) {
            this._groupTweens.goToCase(GroupTweens.CaseBaseToFreespins);
        }

        if (GameManager.freespinsResponse.freeSpins.remainingCount == 1) {
            FreespinsView.wasRespin = true;
        } else {
            FreespinsView.wasRespin = false;
        }

        this.visible = true;

        this._introDelay.start(FreespinsView.INTRO_DELAY);
    }

    introFinished(): void {
        StageController.baseStage = false;
        StageController.freespinStage = true;

        this.visible = true;

        PlayList.resetVolume("SND_FSLoop");
        PlayList.loop("SND_FSLoop");

        this._controller.introFinished();
    }

    outro(): void {
        StageController.freespinStage = true;

        this.visible = true;

        AmbientBackground.changeAmbientBackground("base", 90);

        if (!FreespinsView.wasRespin) {
            PlayList.stop("SND_FSLoop");
            PlayList.stop("SND_FSWait");
        }
        PlayList.play("SND_Transition");

        if (this._fsMessageEnd != null && !this._fsMessageEnd.classList.contains(HtmlController.DisabledClass)) {
            this._fsMessageEnd.classList.add(HtmlController.DisabledClass);
        }

        if (!FreespinsView.wasRespin) {
            if (
                this._fsMessageEndNoWin != null &&
                !this._fsMessageEndNoWin.classList.contains(HtmlController.DisabledClass)
            ) {
                this._fsMessageEndNoWin.classList.add(HtmlController.DisabledClass);
            }
        }

        if (this._groupTweens) {
            this._groupTweens.goToCase(GroupTweens.CaseFreespinsToBase);
        }

        this._outroDelay.start(FreespinsView.wasRespin ? 0 : FreespinsView.OUTRO_DELAY);
    }

    outroFinished(): void {
        StageController.freespinStage = false;

        this.visible = false;

        // this.allSwitchElementToCase("EMPTY");

        this._winAnimator.reset(true);

        this._controller.outroFinished();
    }

    enable(intro: boolean = false): void {
        // PlayList.play('SND_FSWin');
        PlayList.loop("SND_FSWaitIn");
        PlayList.stop("SND_BaseRecapLoop");

        if (intro) {
            this.enableDelay(true);
        } else {
            this._enableDelay.start(70);
        }
    }

    enableDelay(intro: boolean = false): void {
        if (
            (GameManager.freespinsResponse &&
                GameManager.freespinsResponse.customData &&
                GameManager.freespinsResponse.customData.respin == true) ||
            (GameManager.freespinsResponse.freeSpins.totalCount == 1 &&
                GameManager.freespinsResponse.freeSpins.remainingCount == 1)
        ) {
            ModuleManager.showMessage(Communication.Game.GameModules.Base, "RESPIN");
            PlayList.stop("SND_FSLoop");
            PlayList.resetVolume("SND_FSLoop");
            FreespinsView.wasRespin = true;
        } else {
            ModuleManager.showMessage(Communication.Game.GameModules.Base, "NORMAL");
            FreespinsView.wasRespin = false;

            if (this._fsMessageStart != null && this._fsMessageStart.classList.contains(HtmlController.DisabledClass)) {
                this._fsMessageStart.classList.remove(HtmlController.DisabledClass);
                this._fsMessageStart.classList.remove("_noAnim");
            }
        }
    }

    // retrigger feature
    private retrigger(init: boolean = false): void {
        if (!this.isLastFreespin()) {
            if (GameManager.freespinsResponse.wins != null && GameManager.freespinsResponse.wins.length > 0) {
                for (let i: number = 0; i < GameManager.freespinsResponse.wins.length; i++) {
                    if (GameManager.freespinsResponse.wins[i].type === Communication.Game.WinType.freeSpins) {
                        if (
                            this._fsMessageStart != null &&
                            this._fsMessageStart.classList.contains(HtmlController.DisabledClass)
                        ) {
                            if (init) {
                                this.retriggerDelay(true);
                            } else {
                                this._retriggerDelay.start(60);
                            }
                        }
                    }
                }
            }
        }
    }

    private retriggerDelay(force: boolean = false) {
        if (GameManager.freespinsResponse.freeSpins.remainingCount == 1 && FreespinsView.wasRespin) {
            ModuleManager.showMessage(Communication.Game.GameModules.Freespins, "RESPIN");
        } else {
            FreespinsView.wasRespin = false;
            let added = 0;
            if (GameManager.freespinsResponse.wins && GameManager.freespinsResponse.wins.length) {
                GameManager.freespinsResponse.wins.forEach((win) => {
                    if (win.customData && !isNaN(win.customData.count)) {
                        added += win.customData.count;
                    }
                });
            }
            ModuleManager.showMessage(
                Communication.Game.GameModules.Freespins,
                added == 1 ? "RETRIGGER_SINGLE" : "RETRIGGER",
            );
            this._fsMessageStart.classList.remove(HtmlController.DisabledClass);
            this._fsMessageStart.classList.remove("_noAnim");
        }
    }

    private isLastFreespin(): boolean {
        if (DataManager.GameLimit) {
            return true;
        }

        return (
            GameManager.freespinsResponse.freeSpins != null &&
            GameManager.freespinsResponse.freeSpins.remainingCount != null &&
            GameManager.freespinsResponse.freeSpins.remainingCount === 0
        );
    }

    private allSwitchElementToCase(caseType: string): void {
        for (let key in this.childs) {
            if (this.childs[key] instanceof SwitchElement) {
                let elem: SwitchElement = this.childs[key];

                if (elem.hasCase(caseType)) {
                    elem.goToCase(caseType);
                }
            }
        }
    }

    showMessage(type: "NORMAL" | "END" | "RETRIGGER" | "RETRIGGER_SINGLE" | "FORCE" | "RESPIN" | "RESPIN_INIT"): void {
        if ((type == "RESPIN" || type == "RESPIN_INIT") && this._respinFX) {
            this._blurContainer.visible = false;
            this._respinFX.visible = true;
            this._respinFX.play();
        }

        this._blurContainer.visible = true;
        this._blurContainer.goToCase("SHOW_" + type);
        this._middleground.goToCase("FS_MESSAGE_SHOWN");
    }

    hideMessage(): void {
        if (this._fsMessageStart != null && !this._fsMessageStart.classList.contains(HtmlController.DisabledClass)) {
            this._fsMessageStart.classList.add(HtmlController.DisabledClass);
        }
        this._blurContainer.goToCase(FreespinsView.wasRespin ? "HIDE_RESPIN" : "HIDE");
    }

    public calculationCounter(actualCounterValue: string): void {
        if (this._winAnimator) {
            this._winAnimator.calculationCounter(actualCounterValue);
        }
    }

    private setMode = function (mode: "landscape" | "portrait" | "extended") {
        switch (mode) {
            case "extended":
                if (this.modeSetting.Extended) {
                    this.updateByNode(this.modeSetting.Extended);
                    break;
                }
            case "portrait":
                if (this.modeSetting.Portrait) {
                    this.updateByNode(this.modeSetting.Portrait);
                    break;
                }
            case "landscape":
                if (this.modeSetting.Landscape) {
                    this.updateByNode(this.modeSetting.Landscape);
                }
                break;
        }
    };
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Freespins/FreespinsView.ts
 **/